/**
 * TenantMarketingConfigTab - Configurazione marketing AI per tenant
 *
 * Salva su Firestore: tenants/{tenantId}/marketing_settings/default
 * Sezioni:
 * 1. Informazioni aziendali per il marketing
 * 2. Configurazione lead form
 * 3. Limiti budget
 * 4. Account collegati (Meta Ads: collega, prova, scollega)
 *
 * ⚠️ La logica del collegamento Meta sta in `shared/marketing/collegamentoMetaAds.ts`, condivisa
 * con la gemella del gestionale (`src/modules/admin/components/tenant-detail/TenantMarketingConfigTab.tsx`):
 * qui resta solo ciò che si MOSTRA. Le due copie non si fondono in un componente solo perché il
 * gestionale scrive i testi con l'i18n (`t()`), che la console non ha e non deve trascinarsi dietro.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Radio,
} from '@mui/material';
import {
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Campaign as CampaignIcon,
  Language as LanguageIcon,
  MonetizationOn as BudgetIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Wifi as WifiIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from '../firebase';
import {
  apriFinestraDialogo,
  collegaMetaAds,
  creaClientCollegamentoMeta,
  CollegamentoMetaInterrotto,
  type AccountMetaDaScegliere,
  type ChiamaCallable,
  type MotivoCollegamentoInterrotto,
} from '@shared/marketing/collegamentoMetaAds';

// ============================================================================
// TYPES
// ============================================================================

interface TenantMarketingConfigTabProps {
  tenantId: string;
}

interface MarketingSettings {
  privacy_policy_url: string;
  website_url: string;
  logo_marketing_url: string;
  lead_form_locale: string;
  thank_you_message: string;
  thank_you_url: string;
  max_daily_budget: number | '';
  max_monthly_budget: number | '';
}

interface AdAccount {
  id: string;
  platform: string;
  account_name?: string;
  account_id?: string;
  is_active: boolean;
  currency?: string;
  timezone?: string;
  connected_by?: string;
  connected_at?: { toDate?: () => Date; _seconds?: number };
  connected_email?: string;
}

type ConnectionTestStatus = 'idle' | 'testing' | 'success' | 'error';
interface ConnectionTestResult {
  status: ConnectionTestStatus;
  message?: string;
  pageName?: string;
}

const DEFAULT_SETTINGS: MarketingSettings = {
  privacy_policy_url: '',
  website_url: '',
  logo_marketing_url: '',
  lead_form_locale: 'it_IT',
  thank_you_message: '',
  thank_you_url: '',
  max_daily_budget: '',
  max_monthly_budget: '',
};

const LOCALE_OPTIONS = [
  { value: 'it_IT', label: 'Italiano' },
  { value: 'en_US', label: 'English' },
  { value: 'de_DE', label: 'Deutsch' },
  { value: 'fr_FR', label: 'Fran\u00e7ais' },
  { value: 'es_ES', label: 'Espa\u00f1ol' },
];

/** Le callable della console, nella forma che vuole il modulo condiviso del collegamento Meta. */
const chiamaCallable: ChiamaCallable = async <T,>(nome: string, dati: Record<string, unknown>): Promise<T> => {
  const funzione = httpsCallable<Record<string, unknown>, T>(getFunctions(undefined, 'europe-west1'), nome);
  return (await funzione(dati)).data;
};

/** Perché il collegamento si è fermato nel browser → la frase che lo spiega. */
const MESSAGGIO_MOTIVO_INTERROTTO: Readonly<Record<MotivoCollegamentoInterrotto, string>> = {
  finestra_bloccata: 'Il browser ha bloccato la finestra di Facebook: consenti le finestre per questo sito e riprova.',
  finestra_chiusa: 'La finestra di Facebook è stata chiusa prima della fine: il collegamento non è avvenuto.',
  accesso_negato: 'Su Facebook l’accesso è stato annullato o negato: il collegamento non è avvenuto.',
  tempo_scaduto: 'Il collegamento con Facebook è scaduto dopo dieci minuti: premi di nuovo «Collega Meta Ads».',
  interrotto: 'Collegamento con Meta interrotto.',
  errore_meta: 'Facebook non ha concesso l’accesso: il collegamento non è avvenuto.',
  server_da_aggiornare:
    'Il collegamento con Meta è in aggiornamento proprio ora: riprova fra qualche minuto, dopo aver ricaricato la pagina.',
};

/** Il testo nella finestra di Facebook mentre il server prepara il dialogo. */
const TESTO_ATTESA_FINESTRA = 'Stiamo preparando l’accesso a Facebook…';

/** Meta scrive `account_status: 1` per un account attivo. */
const ACCOUNT_META_ATTIVO = 1;

// ============================================================================
// COMPONENT
// ============================================================================

export const TenantMarketingConfigTab: React.FC<TenantMarketingConfigTabProps> = ({ tenantId }) => {
  const [settings, setSettings] = useState<MarketingSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
  const [metaTestResult, setMetaTestResult] = useState<ConnectionTestResult>({ status: 'idle' });
  const [googleTestResult, setGoogleTestResult] = useState<ConnectionTestResult>({ status: 'idle' });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [adAccountChoices, setAdAccountChoices] = useState<AccountMetaDaScegliere[]>([]);
  /** Lo `state` con cui scegliere l'account: il token dell'utente Meta resta sul server. */
  const [statoSceltaInSospeso, setStatoSceltaInSospeso] = useState<string | null>(null);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [confermaScollegaAperta, setConfermaScollegaAperta] = useState(false);
  /** Il collegamento in attesa del ritorno da Facebook: si interrompe se la scheda si chiude. */
  const attesaCollegamentoRef = useRef<AbortController | null>(null);
  const clientMeta = useMemo(() => creaClientCollegamentoMeta(chiamaCallable, tenantId), [tenantId]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --------------------------------------------------------------------------
  // Load data
  // --------------------------------------------------------------------------

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, 'tenants', tenantId, 'marketing_settings', 'default');
      const snap = await getDoc(settingsRef);
      if (snap.exists()) {
        const data = snap.data();
        setSettings({
          privacy_policy_url: data.privacy_policy_url ?? '',
          website_url: data.website_url ?? '',
          logo_marketing_url: data.logo_marketing_url ?? '',
          lead_form_locale: data.lead_form_locale ?? 'it_IT',
          thank_you_message: data.thank_you_message ?? '',
          thank_you_url: data.thank_you_url ?? '',
          max_daily_budget: data.max_daily_budget ?? '',
          max_monthly_budget: data.max_monthly_budget ?? '',
        });
      }
    } catch (err) {
      console.error('[TenantMarketingConfigTab] Errore caricamento settings:', err);
      setSnackbar({ open: true, message: 'Errore nel caricamento delle impostazioni', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const loadAdAccounts = useCallback(async () => {
    try {
      const accounts: AdAccount[] = [];

      // 1. Cerca nella sub-collection marketing_ad_accounts
      const accountsRef = collection(db, 'tenants', tenantId, 'marketing_ad_accounts');
      const snap = await getDocs(accountsRef);
      snap.docs.forEach((d) => {
        accounts.push({ id: d.id, ...d.data() } as AdAccount);
      });

      // 2. Cerca anche nella collection facebook_ad_configs (usata per la configurazione Meta)
      if (!accounts.some((a) => a.platform === 'meta')) {
        const legacyRef = doc(db, 'facebook_ad_configs', tenantId);
        const legacySnap = await getDoc(legacyRef);
        if (legacySnap.exists()) {
          const data = legacySnap.data();
          if (data.is_active) {
            accounts.push({
              id: legacySnap.id,
              platform: 'meta',
              is_active: true,
              account_name: `Meta Ads - ${data.ad_account_id || ''}`,
              account_id: data.ad_account_id as string,
            });
          }
        }
      }

      setAdAccounts(accounts);
    } catch (err) {
      console.error('[TenantMarketingConfigTab] Errore caricamento ad accounts:', err);
    }
  }, [tenantId]);

  useEffect(() => {
    loadSettings();
    loadAdAccounts();
  }, [loadSettings, loadAdAccounts]);

  // --------------------------------------------------------------------------
  // Handlers
  // --------------------------------------------------------------------------

  const handleChange = (field: keyof MarketingSettings, value: string | number) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const settingsRef = doc(db, 'tenants', tenantId, 'marketing_settings', 'default');
      const dataToSave: Record<string, unknown> = {
        privacy_policy_url: settings.privacy_policy_url,
        website_url: settings.website_url,
        logo_marketing_url: settings.logo_marketing_url,
        lead_form_locale: settings.lead_form_locale,
        thank_you_message: settings.thank_you_message,
        thank_you_url: settings.thank_you_url,
        max_daily_budget: settings.max_daily_budget === '' ? null : Number(settings.max_daily_budget),
        max_monthly_budget: settings.max_monthly_budget === '' ? null : Number(settings.max_monthly_budget),
        updated_at: new Date(),
      };
      await setDoc(settingsRef, dataToSave, { merge: true });
      setSnackbar({ open: true, message: 'Impostazioni salvate con successo', severity: 'success' });
    } catch (err) {
      console.error('[TenantMarketingConfigTab] Errore salvataggio:', err);
      setSnackbar({ open: true, message: 'Errore nel salvataggio delle impostazioni', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const timestamp = Date.now();
      const storageRef = ref(storage, `marketing/${tenantId}/brand/logo_${timestamp}.png`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      setSettings((prev) => ({ ...prev, logo_marketing_url: downloadUrl }));
      setSnackbar({ open: true, message: 'Logo caricato con successo', severity: 'success' });
    } catch (err) {
      console.error('[TenantMarketingConfigTab] Errore upload logo:', err);
      setSnackbar({ open: true, message: 'Errore nel caricamento del logo', severity: 'error' });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // --------------------------------------------------------------------------
  // Render helpers
  // --------------------------------------------------------------------------

  const metaAccounts = adAccounts.filter((a) => a.platform === 'meta' && a.is_active);
  const googleAccounts = adAccounts.filter((a) => a.platform === 'google' && a.is_active);

  /**
   * L'errore di un'azione Meta come frase: il motivo del browser spiegato (con ciò che ha scritto
   * Meta, quando non è un semplice annullamento), quello del server com'è (è già in italiano, e la
   * console è solo in italiano).
   */
  const messaggioErroreMeta = (err: unknown, predefinito: string): string => {
    if (err instanceof CollegamentoMetaInterrotto) {
      const frase = MESSAGGIO_MOTIVO_INTERROTTO[err.motivo];
      const perche = err.dettaglio?.descrizione || err.dettaglio?.errore;
      return err.motivo === 'errore_meta' && perche ? `${frase} Motivo indicato da Meta: ${perche}` : frase;
    }
    return err instanceof Error && err.message ? err.message : predefinito;
  };

  // Se la scheda si chiude mentre si aspetta Facebook, l'attesa si interrompe (e la finestra si chiude).
  useEffect(() => () => attesaCollegamentoRef.current?.abort(), []);

  // Collega Meta Ads via OAuth. ⚠️ La finestra si apre QUI, dentro il clic e prima di ogni `await`:
  // l'indirizzo del dialogo lo prepara il server, e una finestra aperta dopo l'attesa verrebbe
  // fermata dal blocco dei popup del browser.
  const handleConnectMeta = useCallback(() => {
    const finestra = apriFinestraDialogo(window, TESTO_ATTESA_FINESTRA);
    attesaCollegamentoRef.current?.abort();
    const attesa = new AbortController();
    attesaCollegamentoRef.current = attesa;
    setConnecting(true);

    collegaMetaAds({ client: clientMeta, win: window, finestra, segnale: attesa.signal })
      .then((esito) => {
        if (esito.step === 'choose_account') {
          setAdAccountChoices(esito.accounts);
          setStatoSceltaInSospeso(esito.stato);
          setShowAccountDialog(true);
        } else {
          setSnackbar({ open: true, message: `Account Meta collegato: ${esito.accountName ?? ''}`, severity: 'success' });
          loadAdAccounts();
        }
      })
      .catch((err: unknown) => {
        // Interrotto perché la scheda si è chiusa: nessuno legge più l'avviso.
        if (err instanceof CollegamentoMetaInterrotto && err.motivo === 'interrotto') return;
        console.error('[TenantMarketingConfigTab] Collegamento Meta non riuscito:', err);
        setSnackbar({ open: true, message: messaggioErroreMeta(err, 'Collegamento con Meta non riuscito.'), severity: 'error' });
      })
      .finally(() => {
        if (attesaCollegamentoRef.current === attesa) attesaCollegamentoRef.current = null;
        setConnecting(false);
      });
  }, [clientMeta, loadAdAccounts]);

  // Scollega Meta Ads: prima la domanda, dentro l'app (niente finestre native del browser).
  const handleDisconnectMeta = useCallback(() => {
    setConfermaScollegaAperta(true);
  }, []);

  const confermaDisconnectMeta = useCallback(async () => {
    setConfermaScollegaAperta(false);
    setDisconnecting(true);
    try {
      await clientMeta.scollega();
      setSnackbar({ open: true, message: 'Account Meta scollegato', severity: 'success' });
      loadAdAccounts();
    } catch (err) {
      console.error('[TenantMarketingConfigTab] Scollegamento Meta non riuscito:', err);
      setSnackbar({ open: true, message: messaggioErroreMeta(err, 'Scollegamento da Meta non riuscito.'), severity: 'error' });
    } finally {
      setDisconnecting(false);
    }
  }, [clientMeta, loadAdAccounts]);

  // Passo 2: utente ha scelto un Ad Account
  const handleSelectAdAccount = useCallback(async (accountId: string) => {
    if (!statoSceltaInSospeso) return;
    setShowAccountDialog(false);
    setConnecting(true);

    try {
      const esito = await clientMeta.scegliAccount(accountId, statoSceltaInSospeso);
      if (esito.step === 'connected') {
        setSnackbar({ open: true, message: `Account Meta collegato: ${esito.accountName ?? ''}`, severity: 'success' });
        loadAdAccounts();
      }
    } catch (err) {
      console.error('[TenantMarketingConfigTab] Scelta dell’account Meta non riuscita:', err);
      setSnackbar({ open: true, message: messaggioErroreMeta(err, 'Collegamento con Meta non riuscito.'), severity: 'error' });
    } finally {
      setConnecting(false);
      setStatoSceltaInSospeso(null);
      setAdAccountChoices([]);
    }
  }, [statoSceltaInSospeso, clientMeta, loadAdAccounts]);

  const handleTestConnection = useCallback(async (platform: 'meta' | 'google') => {
    const setResult = platform === 'meta' ? setMetaTestResult : setGoogleTestResult;
    setResult({ status: 'testing' });

    try {
      const data = await clientMeta.provaConnessione(platform);
      if (data.success) {
        const details = [
          data.account_name,
          data.page_name ? `Pagina: ${data.page_name}` : null,
          data.currency ? `Valuta: ${data.currency}` : null,
        ].filter(Boolean).join(' · ');
        setResult({ status: 'success', message: details, pageName: data.page_name });
      } else {
        setResult({ status: 'error', message: data.error || 'Prova di connessione non riuscita.' });
      }
    } catch (err) {
      console.error('[TenantMarketingConfigTab] Prova di connessione non riuscita:', err);
      setResult({ status: 'error', message: messaggioErroreMeta(err, 'Prova di connessione non riuscita.') });
    }
  }, [clientMeta]);

  const handleCopyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copiato negli appunti', severity: 'success' });
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Stack spacing={3}>

        {/* ------------------------------------------------------------------ */}
        {/* SECTION 1: Informazioni aziendali */}
        {/* ------------------------------------------------------------------ */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CampaignIcon color="primary" />
              Informazioni aziendali per il marketing
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2.5}>
              <TextField
                label="Privacy policy URL"
                value={settings.privacy_policy_url}
                onChange={(e) => handleChange('privacy_policy_url', e.target.value)}
                helperText="URL della pagina informativa sulla privacy"
                fullWidth
                size="small"
              />

              <TextField
                label="Sito web aziendale"
                value={settings.website_url}
                onChange={(e) => handleChange('website_url', e.target.value)}
                helperText="Usato come fallback per la privacy policy"
                fullWidth
                size="small"
              />

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Logo per le campagne marketing
                </Typography>

                {settings.logo_marketing_url && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <img
                      src={settings.logo_marketing_url}
                      alt="Logo marketing"
                      style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
                <Button
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={18} /> : <UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  size="small"
                >
                  {settings.logo_marketing_url ? 'Sostituisci logo' : 'Carica logo'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* ------------------------------------------------------------------ */}
        {/* SECTION 2: Configurazione lead form */}
        {/* ------------------------------------------------------------------ */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon color="primary" />
              Configurazione lead form
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Lingua default</InputLabel>
                <Select
                  value={settings.lead_form_locale}
                  label="Lingua default"
                  onChange={(e) => handleChange('lead_form_locale', e.target.value)}
                >
                  {LOCALE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Messaggio di ringraziamento"
                value={settings.thank_you_message}
                onChange={(e) => handleChange('thank_you_message', e.target.value)}
                multiline
                rows={3}
                fullWidth
                size="small"
              />

              <TextField
                label="URL pagina di ringraziamento"
                value={settings.thank_you_url}
                onChange={(e) => handleChange('thank_you_url', e.target.value)}
                fullWidth
                size="small"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* ------------------------------------------------------------------ */}
        {/* SECTION 3: Limiti budget */}
        {/* ------------------------------------------------------------------ */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BudgetIcon color="primary" />
              Limiti budget
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2.5}>
              <TextField
                label="Budget massimo giornaliero per campagna"
                type="number"
                value={settings.max_daily_budget}
                onChange={(e) => handleChange('max_daily_budget', e.target.value === '' ? '' : Number(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">&euro;</InputAdornment>,
                }}
                fullWidth
                size="small"
              />

              <TextField
                label="Budget massimo mensile totale"
                type="number"
                value={settings.max_monthly_budget}
                onChange={(e) => handleChange('max_monthly_budget', e.target.value === '' ? '' : Number(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">&euro;</InputAdornment>,
                }}
                fullWidth
                size="small"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* ------------------------------------------------------------------ */}
        {/* SECTION 4: Account collegati */}
        {/* ------------------------------------------------------------------ */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon color="primary" />
              Account collegati
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={3}>
              {/* ── Meta Ads ──────────────────────────────────── */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Meta Ads (Facebook + Instagram)</Typography>
                  {metaAccounts.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={metaTestResult.status === 'testing' ? <CircularProgress size={14} /> : <WifiIcon />}
                      onClick={() => handleTestConnection('meta')}
                      disabled={metaTestResult.status === 'testing'}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      {metaTestResult.status === 'testing' ? 'Test in corso...' : 'Test connessione'}
                    </Button>
                  )}
                </Box>

                {metaAccounts.length > 0 ? (
                  <Stack spacing={1}>
                    {metaAccounts.map((acc) => (
                      <Box key={acc.id} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={acc.is_active ? 'Attivo' : 'Inattivo'}
                                color={acc.is_active ? 'success' : 'error'}
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              <Typography variant="body2" fontWeight={500}>
                                {acc.account_name || 'Meta Ads'}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              color="error"
                              variant="text"
                              onClick={handleDisconnectMeta}
                              disabled={disconnecting}
                              startIcon={disconnecting ? <CircularProgress size={12} color="inherit" /> : undefined}
                              sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                            >
                              {disconnecting ? 'Scollegamento...' : 'Scollega'}
                            </Button>
                          </Box>
                          {acc.account_id && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Ad Account ID: <strong>{acc.account_id}</strong>
                              </Typography>
                              <Button size="small" sx={{ minWidth: 24, p: 0 }} onClick={() => handleCopyToClipboard(acc.account_id!)}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </Button>
                            </Box>
                          )}
                          {acc.currency && (
                            <Typography variant="caption" color="text.secondary">
                              Valuta: {acc.currency} — Timezone: {acc.timezone || 'Europe/Rome'}
                            </Typography>
                          )}
                          {acc.connected_at && (
                            <Typography variant="caption" color="text.secondary">
                              Collegato il: {acc.connected_at.toDate
                                ? acc.connected_at.toDate().toLocaleDateString('it-IT')
                                : acc.connected_at._seconds
                                  ? new Date(acc.connected_at._seconds * 1000).toLocaleDateString('it-IT')
                                  : '-'}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    ))}

                    {/* Risultato test connessione Meta */}
                    {metaTestResult.status !== 'idle' && metaTestResult.status !== 'testing' && (
                      <Box sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: metaTestResult.status === 'success' ? 'success.50' : 'error.50',
                        border: '1px solid',
                        borderColor: metaTestResult.status === 'success' ? 'success.200' : 'error.200',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}>
                        {metaTestResult.status === 'success'
                          ? <CheckCircleIcon color="success" sx={{ fontSize: 18, mt: 0.25 }} />
                          : <ErrorIcon color="error" sx={{ fontSize: 18, mt: 0.25 }} />
                        }
                        <Typography variant="caption" sx={{ color: metaTestResult.status === 'success' ? 'success.main' : 'error.main' }}>
                          {metaTestResult.message}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {connecting ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          Collegamento in corso... stiamo configurando il tuo account Meta Ads.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', flex: 1 }}>
                          Nessun account Meta collegato. Collega il tuo Ad Account per creare campagne su Facebook e Instagram.
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleConnectMeta}
                          startIcon={<LinkIcon />}
                          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                        >
                          Collega Meta Ads
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </Box>

              <Divider />

              {/* ── Google Ads ──────────────────────────────────── */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Google Ads</Typography>
                  {googleAccounts.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={googleTestResult.status === 'testing' ? <CircularProgress size={14} /> : <WifiIcon />}
                      onClick={() => handleTestConnection('google')}
                      disabled={googleTestResult.status === 'testing'}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      {googleTestResult.status === 'testing' ? 'Test in corso...' : 'Test connessione'}
                    </Button>
                  )}
                </Box>

                {googleAccounts.length > 0 ? (
                  <Stack spacing={1}>
                    {googleAccounts.map((acc) => (
                      <Box key={acc.id} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={acc.is_active ? 'Attivo' : 'Inattivo'}
                              color={acc.is_active ? 'success' : 'error'}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            <Typography variant="body2" fontWeight={500}>
                              {acc.account_name || 'Google Ads'}
                            </Typography>
                          </Box>
                          {acc.account_id && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Customer ID: <strong>{acc.account_id}</strong>
                              </Typography>
                              <Button size="small" sx={{ minWidth: 24, p: 0 }} onClick={() => handleCopyToClipboard(acc.account_id!)}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </Button>
                            </Box>
                          )}
                          {acc.connected_email && (
                            <Typography variant="caption" color="text.secondary">
                              Email collegata: {acc.connected_email}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    ))}

                    {/* Risultato test connessione Google */}
                    {googleTestResult.status !== 'idle' && googleTestResult.status !== 'testing' && (
                      <Box sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: googleTestResult.status === 'success' ? 'success.50' : 'error.50',
                        border: '1px solid',
                        borderColor: googleTestResult.status === 'success' ? 'success.200' : 'error.200',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}>
                        {googleTestResult.status === 'success'
                          ? <CheckCircleIcon color="success" sx={{ fontSize: 18, mt: 0.25 }} />
                          : <ErrorIcon color="error" sx={{ fontSize: 18, mt: 0.25 }} />
                        }
                        <Typography variant="caption" sx={{ color: googleTestResult.status === 'success' ? 'success.main' : 'error.main' }}>
                          {googleTestResult.message}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Nessun account Google Ads collegato. Il Developer Token e in attesa di approvazione da Google (richiesto il 12/04/2026).
                  </Typography>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* ------------------------------------------------------------------ */}
        {/* Dialog selezione Ad Account Meta */}
        {/* ------------------------------------------------------------------ */}
        <Dialog
          open={showAccountDialog}
          onClose={() => { setShowAccountDialog(false); setStatoSceltaInSospeso(null); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Seleziona un Ad Account</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sono stati trovati {adAccountChoices.length} Ad Account collegati al tuo profilo Facebook. Seleziona quello da usare per le campagne:
            </Typography>
            <List sx={{ mx: -2 }}>
              {adAccountChoices.map((acc) => (
                <ListItemButton
                  key={acc.id}
                  onClick={() => handleSelectAdAccount(acc.id)}
                  sx={{ borderRadius: 2, mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Radio checked={false} size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{acc.name}</Typography>
                        <Chip
                          label={acc.status_label}
                          size="small"
                          color={acc.account_status === ACCOUNT_META_ATTIVO ? 'success' : 'default'}
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </Box>
                    }
                    secondary={`${acc.id} — ${acc.currency} — ${acc.timezone}`}
                  />
                </ListItemButton>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setShowAccountDialog(false); setStatoSceltaInSospeso(null); }}>
              Annulla
            </Button>
          </DialogActions>
        </Dialog>

        {/* Conferma dello scollegamento: dentro l'app, mai `window.confirm` (blocca la pagina). */}
        <Dialog
          open={confermaScollegaAperta}
          onClose={() => setConfermaScollegaAperta(false)}
          maxWidth="xs"
          fullWidth
          aria-labelledby="conferma-scollega-meta-titolo"
        >
          <DialogTitle id="conferma-scollega-meta-titolo">Scollegare l’account Meta Ads?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Le campagne attive continueranno a girare su Meta, ma da qui non si potranno più gestire.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setConfermaScollegaAperta(false)} sx={{ minHeight: 40, minWidth: 100 }}>
              Annulla
            </Button>
            <Button onClick={confermaDisconnectMeta} variant="contained" color="error" sx={{ minHeight: 40, minWidth: 100 }}>
              Scollega
            </Button>
          </DialogActions>
        </Dialog>

        {/* ------------------------------------------------------------------ */}
        {/* Save button */}
        {/* ------------------------------------------------------------------ */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            Salva
          </Button>
        </Box>
      </Stack>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TenantMarketingConfigTab;
